import { useEffect, useState, useContext, useCallback } from "react";
import styles from "./styles.module.css";
import toast from "react-hot-toast";
import { TextField } from "@mui/material";
import { roundOff, customerAmountFormat } from "helpers/NumberFormatHelper";
import ShopContext from "context/ShopContext";
import * as ShiftHelper from "helpers/ShiftHelper";
import * as SalesHelper from "helpers/SalesHelper";
import {
  PAYMENT_MODES,
  CASHBOOK_ENTRY_TYPE,
  KEYPAD_BUTTONS,
  KEYBOARD_KEYS,
  EVENTS,
  SCANNER_RESTRICTED_INPUTS,
} from "const";
import { eventsEmitter } from "utils/events";
import CashIcon from "images/svg/cash.svg";
import GpayIcon from "images/svg/gpay.svg";
import PhonePayIcon from "images/svg/phonePay.svg";
import CardsIcon from "images/svg/card.svg";
import BackIcon from "images/svg/back.svg";
import CloseIcon from "images/svg/close-circle.svg";
import DateTimeHelpers from "helpers/DateTimeHelpers";

const paymentIcons = {
  [PAYMENT_MODES.CASH]: CashIcon,
  [PAYMENT_MODES.UPI]: GpayIcon,
  [PAYMENT_MODES.GPAY]: GpayIcon,
  [PAYMENT_MODES.PHONEPAY]: PhonePayIcon,
  [PAYMENT_MODES.CARD]: CardsIcon,
};

function AddPaymentToVendor({ addPaymentToVendor, currentBalance, vendor, onClose }) {
  const { selectedShop, paymentModes: contextModes } = useContext(ShopContext);

  const [paymentModes, setPaymentModes] = useState([]);
  const [activeMode, setActiveMode] = useState(null);
  const [description, setDescription] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const shiftObj = ShiftHelper.getCurrentShiftId();
  const shiftId = shiftObj?.id;

  const calculateTotal = useCallback((modes) => {
    const total = modes.reduce((acc, mode) => acc + (parseFloat(mode.amount) || 0), 0);
    setTotalAmount(total);
  }, []);

  const handleAddPayment = useCallback(async () => {
    if (submitting) return;

    if (totalAmount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }

    try {
      setSubmitting(true);

      const tenders = paymentModes
        .filter((mode) => Number(mode.amount) > 0)
        .map((mode) => ({
          amount: Number(mode.amount),
          payment_mode: {
            id: mode.id,
            is_active: mode.is_enabled ?? true,
            name: mode.name,
            order: mode.order,
          },
        }));

      const now = DateTimeHelpers.getLocalISOString(new Date());

      const payload = {
        debit: totalAmount,
        note: description || "Payment",
        payment_time: now,
        shift_id: shiftId,
        tenders,
      };

      await addPaymentToVendor(payload);

      const dbEntries = tenders.map((tender) => ({
        shift_id: Number(shiftId),
        local_id: Number(tender.payment_mode.id),
        name: tender.payment_mode.name,
        amount: 0,
        debit: tender.amount,
        description: `${CASHBOOK_ENTRY_TYPE.VENDOR_PAYMENT}: ${description || "Payment"}`,
        type: CASHBOOK_ENTRY_TYPE.VENDOR_PAYMENT,
        time: now,
        vendor_name: vendor?.name || "",
      }));

      await Promise.all(dbEntries.map((entry) => ShiftHelper.addShiftPayment(entry)));

      toast.success("Payment added successfully");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Failed to add payment");
    } finally {
      setSubmitting(false);
    }
  }, [addPaymentToVendor, description, onClose, paymentModes, shiftId, submitting, totalAmount, vendor]);

  const handleKeypadInput = useCallback(
    (val) => {
      setPaymentModes((prevModes) => {
        const updatedModes = prevModes.map((mode) => {
          if (mode.label === activeMode) {
            const currentStr = (mode.amount ?? "0").toString();
            let newAmount = mode.amount;

            if (val === KEYPAD_BUTTONS.CLEAR || val === KEYPAD_BUTTONS.BACKSPACE) {
              newAmount = "0";
            } else if (val === KEYPAD_BUTTONS.DELETE) {
              const sliced = currentStr.slice(0, -1);
              newAmount = sliced === "" || sliced === "-" ? "0" : sliced;
            } else if (val === KEYPAD_BUTTONS.PLUS_MINUS) {
              if (currentStr.startsWith("-")) {
                newAmount = currentStr.slice(1) || "0";
              } else {
                newAmount = currentStr === "0" || currentStr === "" ? "-" : `-${currentStr}`;
              }
            } else if (typeof val === "string" && val.startsWith("+")) {
              const increment = Number(val.replace("+", "")) || 0;
              newAmount = ((parseFloat(currentStr) || 0) + increment).toString();
            } else if (val === KEYPAD_BUTTONS.DOT || val === ".") {
              if (currentStr === "-") newAmount = "-0.";
              else if (currentStr === "" || currentStr === "0") newAmount = "0.";
              else if (!currentStr.includes(".")) newAmount = `${currentStr}.`;
            } else if (typeof val === "string" && !isNaN(val) && val.length === 1) {
              if (currentStr === "0") newAmount = val;
              else if (currentStr === "-") newAmount = `-${val}`;
              else newAmount = `${currentStr}${val}`;
            }

            return { ...mode, amount: newAmount };
          }
          return mode;
        });

        calculateTotal(updatedModes);
        return updatedModes;
      });
    },
    [activeMode, calculateTotal],
  );

  useEffect(() => {
    if (contextModes && contextModes.length > 0) {
      const modesWithAmounts = contextModes
        .filter(
          (m) =>
            m.name.toLowerCase() !== PAYMENT_MODES.CREDIT.toLowerCase() &&
            m.name.toLowerCase() !== PAYMENT_MODES.DISCOUNT.toLowerCase() &&
            m.name.toLowerCase() !== PAYMENT_MODES.POINTS.toLowerCase(),
        )
        .map((mode) => {
          const modeName = typeof mode === "string" ? mode : mode.name;
          return {
            ...mode,
            id: mode.id,
            label: modeName,
            amount: "0",
            icon: paymentIcons[(modeName || "").toLowerCase()] || null,
          };
        });

      setPaymentModes(modesWithAmounts);

      const cashMode = modesWithAmounts.find((m) => m.label.toLowerCase() === "cash");
      setActiveMode(cashMode ? cashMode.label : modesWithAmounts[0]?.label);
    }
  }, [contextModes]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (submitting) return;

      const target = event.target;
      if (target && SCANNER_RESTRICTED_INPUTS.includes(target.tagName)) {
        return;
      }

      if (event.key === KEYBOARD_KEYS.ESCAPE) {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === KEYBOARD_KEYS.ENTER) {
        event.preventDefault();
        handleAddPayment();
        return;
      }

      if (SalesHelper.handleArrowKeysNavigation(event, paymentModes, activeMode, (mode) => setActiveMode(mode.label))) {
        return;
      }

      SalesHelper.handleKeypadKeysInput(event, activeMode, handleKeypadInput);
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [submitting, onClose, handleAddPayment, paymentModes, activeMode, handleKeypadInput]);

  const finalBalance = Number(currentBalance) + Number(totalAmount || 0);

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper} onClick={(e) => e.stopPropagation()}>
        <div className={styles.headerSection}>
          <div className={styles.headerTitle}>
            <h2>Add Payment</h2>
            <h4>Pay vendor for purchases and track your expenses.</h4>
          </div>
          <img src={CloseIcon} alt="Close" onClick={onClose} style={{ cursor: "pointer", width: "24px" }} />
        </div>

        <div className={styles.descriptionSection} style={{ display: "flex", gap: "10px" }}>
          <TextField
            label="Vendor Balance"
            value={customerAmountFormat(currentBalance)}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{
              flex: 1,
              backgroundColor: "#FFFFFF",
              borderRadius: "10px",
              "& .MuiOutlinedInput-root": {
                height: "64px",
                borderRadius: "10px",
                "& fieldset": { borderColor: "#CDCDCF" },
                "&:hover fieldset": { borderColor: "#CDCDCF" },
                "&.Mui-focused fieldset": { borderColor: "#CDCDCF", borderWidth: "1px" },
              },
            }}
          />
          <TextField
            label="Final Balance"
            value={customerAmountFormat(finalBalance)}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{
              flex: 1,
              backgroundColor: "#FFFFFF",
              borderRadius: "10px",
              "& .MuiOutlinedInput-root": {
                height: "64px",
                borderRadius: "10px",
                "& fieldset": { borderColor: "#CDCDCF" },
                "&:hover fieldset": { borderColor: "#CDCDCF" },
                "&.Mui-focused fieldset": { borderColor: "#CDCDCF", borderWidth: "1px" },
              },
            }}
          />
          <TextField
            label="Description"
            placeholder="Enter description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              flex: 1.5,
              backgroundColor: "#FFFFFF",
              borderRadius: "10px",
              "& .MuiOutlinedInput-root": {
                height: "64px",
                borderRadius: "10px",
                "& fieldset": { borderColor: "#CDCDCF" },
                "&:hover fieldset": { borderColor: "#CDCDCF" },
                "&.Mui-focused fieldset": { borderColor: "#7C7C7F", borderWidth: "1px" },
              },
            }}
          />
        </div>

        <div className={styles.paymentSection}>
          {/* Payment Tabs */}
          <div className={styles.paymentTabs}>
            {paymentModes.map((method, index) => (
              <button
                key={method.label}
                onClick={() => setActiveMode(method.label)}
                className={`${index === 0 ? styles.firstBtn : ""} ${
                  index === paymentModes.length - 1 ? styles.lastBtn : ""
                } ${method.label === activeMode ? styles.activeTab : ""}`}
              >
                <div className={styles.paymentHead}>
                  {method.icon && <img src={method.icon} alt={method.label} className={styles.paymentIcon} />}
                  <h3 className={styles.label}>{method.label}</h3>
                </div>

                <h2 className={styles.amount}>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {roundOff(parseFloat(method.amount) || 0)}
                </h2>
              </button>
            ))}
          </div>

          {/* Keypad Section */}
          <div className={styles.keypad}>
            {/* Row 1 */}
            <button
              className={styles.keyBtn}
              style={{ borderTopLeftRadius: "20px" }}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.ONE)}
            >
              1
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.TWO)}>
              2
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.THREE)}>
              3
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_TEN)}>
              +10
            </button>
            <button
              className={styles.keyBtn}
              style={{ borderTopRightRadius: "20px" }}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_HUNDERD)}
            >
              +100
            </button>

            {/* Row 2 */}
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.FOUR)}>
              4
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.FIVE)}>
              5
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.SIX)}>
              6
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_FIFTY)}>
              +50
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_TWO_HUNDERD)}>
              +200
            </button>

            {/* Row 3 */}
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.SEVEN)}>
              7
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.EIGHT)}>
              8
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.NINE)}>
              9
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_MINUS)}>
              +/-
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_FIVE_HUNDRED)}>
              +500
            </button>

            {/* Row 4 */}
            <button
              className={styles.keyBtn}
              style={{ borderBottomLeftRadius: "20px", backgroundColor: "rgba(226, 225, 228, 1)" }}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.BACKSPACE)}
            >
              <img src={BackIcon} alt="Back" />
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.ZERO)}>
              0
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.DOT)}>
              .
            </button>
            <button
              className={styles.keyBtn}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.DELETE)}
              style={{ backgroundColor: "rgba(226, 225, 228, 1)" }}
            >
              Delete
            </button>
            <button
              className={styles.keyBtn}
              style={{ borderBottomRightRadius: "20px" }}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_ONE_THOUSAND)}
            >
              +1000
            </button>
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <div className={styles.footerBtns}>
              <button className={styles.closeBtn} onClick={onClose}>
                Cancel
              </button>
              <button
                className={`${styles.printBtn} ${submitting ? styles.disabled : ""}`}
                onClick={submitting ? null : handleAddPayment}
              >
                Add Payment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddPaymentToVendor;
